"use client";

import { ArrowLeft } from "lucide-react";
import { useTranslations } from "next-intl";

import { useTournamentStatusLabel } from "@/shared/lib/hooks/use-tournament-status-label";

import { Link } from "@/i18n/navigation";
import type { Tournament } from "@/entities/tournament/model/types";
import { BANNER_CONSTANTS } from "@/shared/config";
import { getBadgeConfig } from "@/shared/config/badge-config";
import { Button } from "@/shared/ui/buttons/button";
import { Badge } from "@/shared/ui/data-display/badge";
import { TournamentActions } from "./tournament-actions";

interface TournamentDetailHeaderProps {
  tournament: Tournament;
}

const TYPE_LABELS: Record<string, string> = {
  Classic: "Classic 5v5",
  Draft: "Draft",
  SoloOneVsOne: "1×1",
  Arena: "Arena 3×6",
};

// KAN-1002: фон берём токеном. Зашитый #0a0a0a — это цвет тёмной темы, и в
// светлой он давал тёмное пятно вместо фона страницы.
const HERO_GRADIENT =
  "linear-gradient(135deg, color-mix(in srgb, var(--primary) 30%, var(--background)) 0%, var(--background) 55%)";

export const TournamentDetailHeader = ({
  tournament,
}: TournamentDetailHeaderProps) => {
  const t = useTranslations("Tournaments");
  const statusLabel = useTournamentStatusLabel();
  const badgeConfig = getBadgeConfig(
    "tournamentStatus",
    tournament.status ?? "Upcoming",
  );

  const hasBannerSrc =
    !!tournament.image?.trim() &&
    !tournament.image.includes("/default-cover.svg");

  const typeLabel =
    (tournament.type ? TYPE_LABELS[tournament.type] : undefined) ??
    tournament.type ??
    tournament.format ??
    "";

  return (
    <div className="mb-8">
      <div
        className="relative w-full overflow-hidden rounded-[14px] min-h-[200px] p-6 sm:min-h-[240px] sm:p-10 md:min-h-[280px] md:p-[64px_48px]"
        // Тот же аспект (4:1), что у карточек турниров/превью настроек (BANNER_CONSTANTS.ASPECT_RATIO),
        // чтобы кроп баннера был одинаковым везде. На узких экранах min-h держит место под контент.
        // KAN: без явного w-full блок без height внутри aspect-ratio на узких экранах
        // выводил ширину ИЗ min-height (200px·4 = 800px) вместо ширины контейнера —
        // ~450px баннера рисовались за пределами вьюпорта (скрыто overflow-x: clip на
        // html/body). w-full фиксирует ширину = контейнеру, aspect-ratio/min-height
        // дальше только выбирают итоговую высоту — на десктопе (где width×0.25 уже ≥
        // min-height) поведение не меняется.
        style={{
          aspectRatio: BANNER_CONSTANTS.ASPECT_RATIO,
          ...(hasBannerSrc ? {} : { background: HERO_GRADIENT }),
        }}
      >
        {hasBannerSrc ? (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${tournament.image})` }}
              aria-hidden
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(135deg, color-mix(in srgb, var(--background) 55%, transparent) 0%, color-mix(in srgb, var(--background) 85%, transparent) 100%)",
              }}
              aria-hidden
            />
          </>
        ) : null}

        <div className="absolute left-4 bottom-4 max-w-[85%] sm:left-6 sm:bottom-6 sm:max-w-[78%] md:left-12 md:bottom-8 md:max-w-[70%]">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={badgeConfig.variant} className="shrink-0">
              {statusLabel(tournament.status ?? "Upcoming")}
            </Badge>
            <span className="text-xs font-bold uppercase tracking-[0.04em] text-white/55">
              League of Legends{typeLabel ? ` · ${typeLabel}` : ""}
            </span>
          </div>
          <h1
            // KAN: на мобильных экранах фикс. 56px не помещался (заголовок уезжал за
            // край, «AUGUST DR» / «MODE») — размер теперь скейлится по брейкпоинтам,
            // 56px остаётся только от md и выше (как в исходном макете).
            className="mt-3 text-[32px] font-normal leading-[1.15] text-white max-w-[13ch] break-words sm:text-[40px] md:text-[56px] md:leading-[1.2]"
            style={{ fontFamily: "var(--font-logo)" }}
          >
            {tournament.name}
          </h1>
        </div>
      </div>

      {/* KAN: Button (shared/ui/buttons/button.tsx) всегда shrink-0 + whitespace-nowrap —
          при узком экране и/или длинном тексте локали ряд без flex-wrap не сжимался,
          а вылезал за край (на 320px «Войти для участия» уезжало под overflow-x: clip
          и было некликабельно). flex-wrap переносит вторую кнопку на новую строку
          вместо обрезки. */}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/tournaments" className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            {t("header.backToTournaments")}
          </Link>
        </Button>
        <TournamentActions tournament={tournament} />
      </div>
    </div>
  );
};
